import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import AppRouter from '@/routes/AppRouter';
import FeedbackLauncher from '@/components/FeedbackLauncher';
import IdleLogoutManager from '@/components/IdleLogoutManager';
import { Toaster } from '@/components/ui/sonner';
import { DiagnosticBadge } from '@/components/DiagnosticBadge';
import '@/App.css';

/** Tiny CORS diagnostic — only shown when ?corsdiag=1 is in the URL. Zero effect otherwise.
 *  Uses XMLHttpRequest to bypass service worker postMessage cloning issues. */
function CorsDiag() {
  const [results, setResults] = React.useState(null);
  const params = new URLSearchParams(window.location.search);
  if (params.get('corsdiag') !== '1') return null;

  const PREVIEW = process.env.REACT_APP_BACKEND_URL;
  const targets = [
    { label: 'Relative', url: '/api/homepage-content' },
    { label: 'Preview', url: `${PREVIEW}/api/homepage-content` },
    { label: 'Prod bare', url: 'https://sergeanttap.com/api/homepage-content' },
    { label: 'Prod www', url: 'https://www.sergeanttap.com/api/homepage-content' },
  ];

  const xhrTest = (url) => new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.withCredentials = true;
    xhr.onload = () => {
      resolve({
        reached: true,
        status: xhr.status,
        acao: xhr.getResponseHeader('access-control-allow-origin') || '(not readable)',
      });
    };
    xhr.onerror = () => {
      resolve({ reached: false, error: 'Network error / CORS blocked' });
    };
    xhr.ontimeout = () => {
      resolve({ reached: false, error: 'Timeout' });
    };
    xhr.timeout = 8000;
    try { xhr.send(); } catch (e) { resolve({ reached: false, error: e.message }); }
  });

  const run = async () => {
    const out = [];
    for (const t of targets) {
      const r = await xhrTest(t.url);
      out.push({ ...t, ...r });
    }
    setResults(out);
  };

  const S = { ok: { color: '#0f0' }, fail: { color: '#f44' }, dim: { color: '#888' } };

  return (
    <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 99999, background: '#111', color: '#0f0', fontFamily: 'monospace', fontSize: 11, padding: 8, maxHeight: '45vh', overflow: 'auto' }}>
      <div><b>CORS Diagnostic</b> &nbsp; origin: <b>{window.location.origin}</b></div>
      {!results ? (
        <button onClick={run} style={{ margin: '4px 0', padding: '2px 8px', background: '#333', color: '#0f0', border: '1px solid #0f0', cursor: 'pointer' }}>Run Tests</button>
      ) : (
        <div style={{ marginTop: 4 }}>
          {results.map((r, i) => (
            <div key={i} style={{ borderTop: '1px solid #333', padding: '4px 0' }}>
              <span style={r.reached ? S.ok : S.fail}>{r.reached ? 'PASS' : 'FAIL'}</span>{' '}
              <b>{r.label}</b>{' '}
              <span style={S.dim}>{r.url}</span><br />
              {r.reached ? (
                <span>&nbsp; status={r.status} &nbsp; ACAO={r.acao}</span>
              ) : (
                <span style={S.fail}>&nbsp; {r.error}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        <FeedbackLauncher />
        <IdleLogoutManager />
      </BrowserRouter>
      <Toaster position="top-right" duration={2000} />
      <CorsDiag />
      <DiagnosticBadge />
    </div>
  );
}

export default App;
